import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MapPin, Hammer, Hand, Users } from "lucide-react";
import api from "@/lib/api";
import AIContentBlock from "@/components/AIContentBlock";
import PronounceButton from "@/components/PronounceButton";

const Fact = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-2xl border border-slate-100 p-5">
    <p className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2 mb-2"><Icon className="w-4 h-4 text-pink-500" /> {label}</p>
    <p className="text-sm text-slate-700 leading-relaxed">{value}</p>
  </div>
);

export default function InstrumentDetail() {
  const { slug } = useParams();
  const [inst, setInst] = useState(null);

  useEffect(() => {
    setInst(null);
    api.get(`/instruments/${slug}`).then((r) => setInst(r.data)).catch(console.error);
  }, [slug]);

  if (!inst) return <div className="min-h-[50vh] flex items-center justify-center"><span className="eq-bars" style={{ height: 36 }}><span /><span /><span /><span /><span /></span></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid={`instrument-page-${slug}`}>
      <div className="grid lg:grid-cols-2 gap-10 items-start">
        <img src={inst.image} alt={inst.name} className="rounded-3xl w-full h-96 object-cover shadow-xl" />
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-purple-600 mb-2">{inst.classification}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900 flex items-center gap-3 flex-wrap">{inst.name} <PronounceButton text={inst.name} /></h1>
          <p className="mt-2 text-sm text-slate-500 flex items-center gap-1"><MapPin className="w-4 h-4 text-pink-500" /> Origin: {inst.origin}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {inst.regions?.map((r) => <span key={r} className="px-3 py-1 rounded-full bg-pink-50 text-pink-700 text-xs font-bold">{r}</span>)}
          </div>
          <p className="mt-5 text-slate-600 leading-relaxed">{inst.description}</p>
          <AIContentBlock pageKey={`instrument-${slug}-craft`} topic={`The craft, materials and playing technique of the ${inst.name}`} compact />
          <div className="grid sm:grid-cols-1 gap-4 mt-6">
            <Fact icon={Hammer} label="Construction & materials" value={inst.materials} />
            <Fact icon={Hand} label="Playing technique" value={inst.technique} />
            <Fact icon={Users} label="Notable players" value={inst.notable_players?.join(" · ")} />
          </div>
        </div>
      </div>

      <AIContentBlock pageKey={`instrument-${slug}`} topic={`The ${inst.name} — ${inst.classification} from ${inst.origin}`} imgQuery={`${inst.name} musical instrument`} />
    </div>
  );
}
